// =============================================================================
// nav/schema.ts — 声明式侧栏菜单配置（按 docs/ui_sidebar §3.2 模板 02 移植）
// mailman 菜单 = 声明式配置；新增模块只需追加 NavGroup / NavItem。
// 权限说明：mailman 登录接口仅允许 admin（handlers.rs login 403 兜底），
//           故菜单项不设 permission（缺省 = 仅登录可见）；结构保留文档的
//           PermissionKey 机制以便未来接入 RBAC。
// =============================================================================

import type { ReactElement } from 'react'
import {
  Home20Regular,
  Globe20Regular,
  Apps20Regular,
  TextBulletListSquare20Regular,
  Save20Regular,
  History20Regular,
  Rocket20Regular,
  DocumentText20Regular,
  MailInbox20Regular,
  MailRegular,
  ArrowHookUpLeft20Regular,
  Code20Regular,
  Recycle20Regular,
  Settings20Regular,
  Shield20Regular,
} from '@fluentui/react-icons'
import { ROUTES } from '@/routes'

/** "*" 表示全部权限（admin 角色）；其余为具体权限 key */
export type PermissionKey = string

export interface NavItem {
  /** 路由 path，必须等于 ROUTES 中的某个常量 */
  path: string
  /** 显示名 */
  label: string
  /** Fluent UI 20px 图标组件 */
  icon: ReactElement
  /** 关联权限；缺省表示该路由仅受登录保护 */
  permission?: PermissionKey
  /** 折叠态 tooltip 详细描述（用于搜索 / a11y） */
  description?: string
}

export interface NavGroup {
  /** section 排序 key，越小越靠前 */
  order: number
  /** 中文 section 名 */
  section: string
  items: NavItem[]
}

// -----------------------------------------------------------------------------
// 顶部独立项（不在分组内呈现，常用于 Dashboard）
// -----------------------------------------------------------------------------
export const NAV_TOP: NavItem = {
  path: ROUTES.HOME,
  label: '上线配置',
  icon: <Rocket20Regular />,
  description: '上线配置指引（默认主页）',
}

// -----------------------------------------------------------------------------
// 菜单分组（order 越小越靠前）
// -----------------------------------------------------------------------------
export const NAV: NavGroup[] = [
  {
    order: 0,
    section: '系统总览',
    items: [
      { path: ROUTES.STATS, label: '状态', icon: <Home20Regular />, description: '系统总览、关键指标与运行状态' },
    ],
  },
  {
    order: 1,
    section: '收件域',
    items: [
      { path: ROUTES.DOMAINS, label: '收件域', icon: <Globe20Regular />, description: '收件域及邮箱/别名/DKIM/TLS/收信设置/写信发送/发送记录的集中管理' },
    ],
  },
  {
    order: 2,
    section: '收信',
    items: [
      { path: ROUTES.MAILBOX, label: '收件箱/邮件查询', icon: <MailInbox20Regular />, description: '收件查询与邮件内容查看' },
      { path: ROUTES.SEND_LOGS, label: '发件箱', icon: <MailRegular />, description: '账号级发信记录（已发送）与发起新发信' },
      { path: ROUTES.ANTISPAM, label: '反垃圾设置', icon: <Shield20Regular />, description: '拒收域名黑名单、DNSBL 与入站间隔，保存后实时生效，无需重启' },
    ],
  },
  {
    order: 3,
    section: '第三方接入',
    items: [
      { path: ROUTES.CLIENTS, label: '接入客户端', icon: <Apps20Regular />, description: 'SMTP/API 客户端接入' },
      { path: ROUTES.INBOUND_RULES, label: '收件转发到第三方', icon: <ArrowHookUpLeft20Regular />, description: '收件 webhook 转发规则' },
      { path: ROUTES.TEMPLATES, label: '邮件模板', icon: <DocumentText20Regular />, description: '发信模板与变量' },
      { path: ROUTES.API_DOCS, label: 'API 文档/调试器', icon: <Code20Regular />, description: '对外发信 API 契约与试发' },
    ],
  },
  {
    order: 4,
    section: '数据与归档',
    items: [
      { path: ROUTES.QUEUE, label: '队列管理', icon: <TextBulletListSquare20Regular />, description: '跨域投递队列与重试' },
      { path: ROUTES.BACKUPS, label: '备份管理', icon: <Save20Regular />, description: '数据备份与下载' },
      { path: ROUTES.AUDIT_LOGS, label: '审计日志', icon: <History20Regular />, description: '管理操作审计' },
      { path: ROUTES.RECYCLE_BIN, label: '回收站', icon: <Recycle20Regular />, description: '查看和恢复软删除的数据' },
    ],
  },
  {
    order: 5,
    section: '设置',
    items: [
      { path: ROUTES.SETTINGS, label: '系统设置', icon: <Settings20Regular />, description: '基本信息、AI 服务商、队列清理与保留归档策略' },
    ],
  },
]
